import { useProjectParams } from '@hooks/useProjectParams';
import { WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { WorkOrderStructurePageLayout } from 'pages/Project/Common/WorkOrderStructure/WorkOrderStructurePageLayout';
import { methodologyMapReverse } from 'types/methodology';
import { workOrderChangeDescriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import type React from 'react';
import { useMemo } from 'react';

import { UerWorkOrderChangeObjectEditComponent } from './components/UerWorkOrderChangeObjectEditComponent';
import { useGetUerWorkOrderChangeStructureDataSource } from './hooks/useGetUerWorkOrderChangeStructureDataSource';
import { useUerRemoveWorkOrderChangeObject } from './hooks/useUerRemoveWorkOrderChangeObject';
import { useUerWorkOrderChangeEditObjectDataSource } from './hooks/useUerWorkOrderChangeEditObjectDataSource';
import { useUerWorkOrderChangeGetIncludedCountData } from './hooks/useUerWorkOrderChangeGetIncludedCountData';

import '../workOrderChange.css';

export const UerWorkOrderChangeStructure: React.FC = () => {
    const { id: projectId, methodology, orderChangeId } = useProjectParams();
    const { projectData } = useProjectData(projectId);
    const { workOrderChangeUer: workOrderChangeRules } = useAccess();
    const workOrderChangeId = useMemo(() => Number(orderChangeId), [orderChangeId]);

    const { change: currentChange, workOrder: currentWorkOrder } = useMemo(() => {
        let change;
        let workOrder;

        for (let i = 0; i < (projectData?.contractNavigation?.data?.workOrderNavs?.length ?? 0); i += 1) {
            const nav = projectData?.contractNavigation?.data?.workOrderNavs?.[i];

            change = nav?.workOrderChanges?.find((c) => c.id === workOrderChangeId);

            if (change) {
                workOrder = nav;
                break;
            }
        }

        return { change, workOrder };
    }, [projectData?.contractNavigation?.data?.workOrderNavs, workOrderChangeId]);

    const isReadonly = useMemo(
        () =>
            currentChange?.status !== WorkOrderChangeStatusEnum.OnFormation ||
            currentWorkOrder?.status === WorkOrderStatusEnum.Delayed,
        [currentChange?.status, currentWorkOrder?.status],
    );

    const titleWorkOrderChange = useMemo(
        () =>
            `Изм ${workOrderChangeDescriptionNumberAndDate(
                currentChange?.workOrderChangeNumber,
                currentChange?.signingDate,
                currentChange?.workOrderNumber,
            )}`,
        [currentChange?.signingDate, currentChange?.workOrderChangeNumber, currentChange?.workOrderNumber],
    );

    return (
        <WorkOrderStructurePageLayout
            isReadonly={isReadonly}
            canEditObjectCode={workOrderChangeRules.canEditObjectCode && !isReadonly}
            workOrderOrChangeId={workOrderChangeId}
            methodology={methodologyMapReverse[methodology as string]}
            projectData={projectData}
            useGetDataSource={useGetUerWorkOrderChangeStructureDataSource}
            WorkOrderObjectEditComponent={UerWorkOrderChangeObjectEditComponent}
            WorkOrderSubObjectEditComponent={UerWorkOrderChangeObjectEditComponent}
            workOrderFullNumber={titleWorkOrderChange}
            useWorkOrderEditObjectDataSource={useUerWorkOrderChangeEditObjectDataSource}
            useRemoveWorkOrderObject={useUerRemoveWorkOrderChangeObject}
            useGetIncludedCountData={useUerWorkOrderChangeGetIncludedCountData}
            skipEntityNameInTitle
        />
    );
};

UerWorkOrderChangeStructure.displayName = 'UerWorkOrderChangeStructure';
